import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  MapPin,
  Ruler,
  Money,
  Calendar,
  Envelope,
  Phone,
  ArrowLeft,
  Share,
  Heart,
  House,
  Tree,
  CheckCircle,
  CaretLeft,
  CaretRight
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function LandDetailPage() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);

  const fetchListing = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/land-listings/${id}`);
      setListing(response.data);
    } catch (error) {
      console.error("Error fetching listing:", error);
      toast.error("Listing not found");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  const formatPrice = (listing) => {
    if (!listing) return "";
    if (listing.listing_type === "rent") {
      return `€${listing.price.toLocaleString()} per year`;
    }
    return `€${listing.price.toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: listing.title,
        text: `${listing.acres} acres in ${listing.county}`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF1E5]">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="animate-pulse">
            <div className="h-64 bg-[#E5E7EB] mb-4"></div>
            <div className="h-8 bg-[#E5E7EB] w-3/4 mb-4"></div>
            <div className="h-6 bg-[#E5E7EB] w-1/2"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-[#FFF1E5]">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <MapPin size={64} className="mx-auto text-[#E0D4C8] mb-4" />
          <h1 className="font-serif text-2xl text-[#33302E] mb-2">Listing Not Found</h1>
          <p className="text-[#6B6661] mb-6">This listing may have been removed or sold.</p>
          <Link to="/land" className="text-[#0F766E] hover:underline">
            ← Back to all listings
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const images = listing.images && listing.images.length > 0 ? listing.images : [];

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <Header />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Link */}
        <Link 
          to="/land" 
          className="inline-flex items-center gap-2 text-[#0F766E] hover:text-[#064E3B] mb-6"
        >
          <ArrowLeft size={18} />
          Back to all listings
        </Link>

        {/* Image Gallery */}
        {images.length > 0 ? (
          <div className="relative bg-[#064E3B] h-80 md:h-96 mb-6 overflow-hidden">
            <img 
              src={images[currentImage]} 
              alt={listing.title}
              className="w-full h-full object-cover"
            />
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentImage(i => i > 0 ? i - 1 : images.length - 1)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full hover:bg-white"
                >
                  <CaretLeft size={24} />
                </button>
                <button
                  onClick={() => setCurrentImage(i => i < images.length - 1 ? i + 1 : 0)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full hover:bg-white"
                >
                  <CaretRight size={24} />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImage(index)}
                      className={`w-2 h-2 rounded-full ${index === currentImage ? 'bg-white' : 'bg-white/50'}`}
                    />
                  ))}
                </div>
              </>
            )}
            {/* Badges */}
            <div className="absolute top-4 left-4 flex gap-2">
              <span className={`px-3 py-1 text-sm font-medium ${
                listing.listing_type === "sale" ? "bg-[#0F766E] text-white" :
                listing.listing_type === "rent" ? "bg-[#FBBF24] text-[#022C22]" :
                "bg-[#3B82F6] text-white"
              }`}>
                {listing.listing_type === "sale" ? "For Sale" :
                 listing.listing_type === "rent" ? "For Rent" : "Long Lease"}
              </span>
              {listing.featured && (
                <span className="px-3 py-1 bg-[#DC2626] text-white text-sm font-medium">
                  Featured
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-[#064E3B] h-64 mb-6 flex items-center justify-center">
            <MapPin size={64} className="text-[#0F766E]" />
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Header */}
            <div className="bg-white border border-[#E0D4C8] p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-sm text-[#6B6661] capitalize mb-1">{listing.category?.replace("_", " ")}</p>
                  <h1 className="font-serif text-2xl md:text-3xl text-[#33302E]">
                    {listing.title}
                  </h1>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleShare}
                    className="p-2 border border-[#E0D4C8] hover:bg-[#F9FAFB]"
                  >
                    <Share size={20} className="text-[#6B6661]" />
                  </button>
                  <button className="p-2 border border-[#E0D4C8] hover:bg-[#F9FAFB]">
                    <Heart size={20} className="text-[#6B6661]" />
                  </button>
                </div>
              </div>
              
              <p className="flex items-center gap-2 text-[#6B6661] mb-4">
                <MapPin size={18} />
                {listing.address || listing.county}, Ireland
              </p>

              <div className="flex items-center gap-6 pt-4 border-t border-[#E0D4C8]">
                <div>
                  <p className="text-sm text-[#6B6661]">Price</p>
                  <p className="font-serif text-2xl text-[#0F766E]">{formatPrice(listing)}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B6661]">Size</p>
                  <p className="font-serif text-2xl text-[#33302E]">{listing.acres} acres</p>
                </div>
                {listing.price_per_acre && (
                  <div>
                    <p className="text-sm text-[#6B6661]">Per Acre</p>
                    <p className="font-serif text-xl text-[#33302E]">€{listing.price_per_acre.toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="bg-white border border-[#E0D4C8] p-6">
              <h2 className="font-serif text-xl text-[#33302E] mb-4">Description</h2>
              <p className="text-[#6B6661] whitespace-pre-wrap leading-relaxed">
                {listing.description}
              </p>
            </div>

            {/* Features */}
            {listing.features && listing.features.length > 0 && (
              <div className="bg-white border border-[#E0D4C8] p-6">
                <h2 className="font-serif text-xl text-[#33302E] mb-4">Features</h2>
                <ul className="grid grid-cols-2 gap-2">
                  {listing.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-[#6B6661]">
                      <CheckCircle size={18} className="text-[#0F766E]" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <div className="bg-gradient-to-r from-[#022C22] to-[#064E3B] p-6">
              <h3 className="font-serif text-lg text-white mb-4">Interested?</h3>
              <div className="space-y-3">
                <a
                  href={`mailto:listings@farmwatch.com?subject=Enquiry: ${listing.title}`}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-white text-[#022C22] font-medium hover:bg-[#F0FDF4] transition-colors"
                >
                  <Envelope size={20} />
                  Send Enquiry
                </a>
                <button className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-white text-white font-medium hover:bg-white/10 transition-colors">
                  <Phone size={20} />
                  Request Callback
                </button>
              </div>
            </div>

            {/* Details */}
            <div className="bg-white border border-[#E0D4C8] p-6">
              <h3 className="font-serif text-lg text-[#33302E] mb-4">Details</h3>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-[#6B6661]">Listed</dt>
                  <dd className="text-[#33302E]">{formatDate(listing.created_at)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-[#6B6661]">Views</dt>
                  <dd className="text-[#33302E]">{listing.views || 0}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-[#6B6661]">Reference</dt>
                  <dd className="text-[#33302E] font-mono text-xs">{listing.id}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
